import { ChipLabelItem, Container, FilterButtonStyled, FilterChips } from '@components/Filter';
import { FilterDrawer } from '@components/Filter/FilterDrawer';
import { hasValue } from '@components/Filter/filter.utils';
import { FlexRow } from '@components/FlexRow';
import StarIcon from '@components/Icon/Star';
import StarActiveIcon from '@components/Icon/StarActiveIcon';
import InputSliderRangeStatic from '@components/Input/InputSliderRange/static';
import InputAutocomplete from '@components/InputAutocomplete';
import InputLocationFilter, { LocationFilterValue } from '@components/InputLocationFilter';
import { SearchBarWithNumber } from '@components/SearchbarWithNumber';
import { Box, FormGroup, FormLabel, Rating } from '@mui/material';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import AvailabilityFilter from './filter-fields/AvailabilityFilter';
import CountryFilter from './filter-fields/CountryFilter';
import LanguageFilter from './filter-fields/LanguageFilter';
import PriorityFilter from './filter-fields/PriorityFilter';
import ProfessionFilter from './filter-fields/ProfessionFilter';
import StatusFilter, { getTalentStatusLabel } from './filter-fields/StatusFilter';
import TagFilter from './filter-fields/TagFilter';
import {
  defaultSearchFilters,
  ITalentSearchAvailableFilters,
  ITalentSearchFilter,
  ITalentSearchFilterOption,
  SearchContext,
} from './types';
import { SearchPresetProvider } from './SearchPresetContext';
import { PresetChooseButton, PresetMenu, PresetSaveButton, PresetSaveDialog } from './PresetSelector';

const filterLabels: Record<string, string> = {
  jobOfferScoringStates: 'Scoring',
  resumeReadinessStatuses: 'Etat CV',
  jobOfferScoringRange: 'Plage de score',
  professionIds: 'Métier',
  specialtyIds: 'Savoir-faire',
  softSkillIds: 'Soft Skills',
  certificateIds: 'Certificats',
  diplomaTypes: 'Diplôme',
  experienceRange: 'Expérience',
  expectedSalaryRange: 'Salaire',
  geoLocation: 'Localisation',
  cities: 'Ville',
  countries: 'Pays',
  availability: 'Disponibilité',
  currentStatus: 'Statut',
  contractTypes: 'Type de contrat',
  languages: 'Langues',
  minRating: 'Évaluation',
  tags: 'Tags',
  priority: 'Priorité',
};

const renderSearchChipLabel = ({
  key,
  value,
  label,
  chipLabels,
}: {
  key: string;
  value: any;
  label: string;
  chipLabels?: Record<string, ChipLabelItem[]>;
}): ReactNode | undefined => {
  if (key === 'minRating' && typeof value === 'number' && value > 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {label}:
        <Rating
          readOnly
          value={value}
          size="small"
          icon={<StarActiveIcon style={{ width: '14px', height: '14px' }} />}
          emptyIcon={<StarIcon style={{ width: '14px', height: '14px' }} />}
        />
      </Box>
    );
  }

  if ((key === 'experienceRange' || key === 'expectedSalaryRange') && value && typeof value === 'object') {
    const unit = key === 'expectedSalaryRange' ? '€' : 'ans';
    const min = value.min != null ? `${value.min} ${unit}` : '';
    const max = value.max != null ? `${value.max} ${unit}` : '';
    if (min && max) return `${label}: ${min} - ${max}`;
    if (min) return `${label}: Min ${min}`;
    if (max) return `${label}: Max ${max}`;
  }

  if (key === 'geoLocation' && value && typeof value === 'object' && 'lat' in value) {
    const loc = value as LocationFilterValue;
    const center = loc.label || `${loc.lat?.toFixed(4)}, ${loc.lng?.toFixed(4)}`;
    return `${label}: ${center} (${(loc as any).radius || 30} km)`;
  }

  if (key === 'languages' && value && typeof value === 'object') {
    const languageId = (value as any).languageId;
    const minLevelId = (value as any).minLevelId;
    const languageLabel = chipLabels?.languageIds?.find((item) => item.id === languageId || item.value === languageId)?.label;
    const levelLabel = chipLabels?.languageLevelIds?.find((item) => item.id === minLevelId || item.value === minLevelId)?.label;

    if (languageLabel && levelLabel) return `${label}: ${languageLabel} (${levelLabel}+)`;
    if (languageLabel) return `${label}: ${languageLabel}`;
  }

  if (key === 'currentStatus' && typeof value === 'string') {
    const statusLabel = getTalentStatusLabel(value as any);
    if (typeof statusLabel === 'string') {
      return `${label}: ${statusLabel}`;
    }
  }

  if (key === 'jobOfferScoringStates' && typeof value === 'string') {
    if (value === 'EMPTY') return `${label}: Non score`;
    if (value === 'SCORED') return `${label}: Score`;
  }

  if (key === 'resumeReadinessStatuses' && typeof value === 'string') {
    if (value === 'READY') return `${label}: Pret`;
    if (value === 'CV_NOT_ANALYZED') return `${label}: CV non analyse`;
    if (value === 'CV_MISSING') return `${label}: CV manquant`;
  }

  if (key === 'jobOfferScoringRange' && typeof value === 'object' && value !== null) {
    const range = value as { min?: number | null; max?: number | null };
    if (range.min != null && range.max != null) return `${label}: ${range.min}% - ${range.max}%`;
    if (range.min != null) return `${label}: ≥ ${range.min}%`;
    if (range.max != null) return `${label}: ≤ ${range.max}%`;
  }

  return undefined;
};

interface TalentSearchFilterProps {
  context: SearchContext;
  jobOfferIdExists?: boolean;
  totalDocs: number;
  onQueryChange: (query: string) => void;
  onFilterChange: (filter: ITalentSearchFilter) => void;
  filter: ITalentSearchFilter;
  availableFilters: ITalentSearchAvailableFilters;
  endAdornment?: ReactNode;
}

export const TalentSearchFilter = ({
  context,
  jobOfferIdExists,
  totalDocs,
  onQueryChange,
  onFilterChange,
  filter,
  availableFilters,
  endAdornment,
}: TalentSearchFilterProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const mapChipOptions = (options: ITalentSearchFilterOption[]): ChipLabelItem[] =>
    options.map((option) => ({
      id: option.value,
      value: option.value,
      label: option.label,
    }));

  const chipLabels = useMemo<Record<string, ChipLabelItem[]>>(
    () => ({
      professionIds: mapChipOptions(availableFilters.professionIds),
      specialtyIds: mapChipOptions(availableFilters.specialtyIds),
      softSkillIds: mapChipOptions(availableFilters.softSkillIds),
      certificateIds: mapChipOptions(availableFilters.certificateIds),
      diplomaTypes: mapChipOptions(availableFilters.diplomaTypes),
      cities: mapChipOptions(availableFilters.cities),
      countries: mapChipOptions(availableFilters.countries),
      availability: mapChipOptions(availableFilters.availability),
      currentStatus: mapChipOptions(availableFilters.currentStatus),
      contractTypes: mapChipOptions(availableFilters.contractTypes),
      languageIds: mapChipOptions(availableFilters.languageIds),
      languageLevelIds: mapChipOptions(availableFilters.languageLevelIds),
      tags: mapChipOptions(availableFilters.tags),
      priority: mapChipOptions(availableFilters.priority),
      jobOfferScoringStates: mapChipOptions(availableFilters.jobOfferScoringStates),
      resumeReadinessStatuses: mapChipOptions(availableFilters.resumeReadinessStatuses),
    }),
    [availableFilters],
  );

  const hasFilter = useMemo(() => {
    return Object.entries(filter || {}).some(([key, value]) => {
      if (key === 'query') return false;
      return hasValue(value);
    });
  }, [filter]);

  const methods = useForm({
    defaultValues: defaultSearchFilters,
  });

  useEffect(() => {
    methods.reset(filter);
  }, [filter, methods]);

  const toggleDrawer = (open: boolean) => () => setIsDrawerOpen(open);

  const onApplyFilter = (data: ITalentSearchFilter) => {
    onFilterChange(data);
    setIsDrawerOpen(false);
  };

  const onResetFilter = () => {
    onFilterChange(defaultSearchFilters);
    methods.reset(defaultSearchFilters);
    setIsDrawerOpen(false);
  };

  const formatOptionLabel = (option: ITalentSearchFilterOption) => `${option.label} (${option.count})`;

  const specialtyOptions = availableFilters.specialtyIds.map((option) => ({
    value: option.value,
    label: formatOptionLabel(option),
    groupLabel: option.groupLabel,
  }));

  const softSkillOptions = availableFilters.softSkillIds.map((option) => ({
    value: option.value,
    label: formatOptionLabel(option),
  }));

  const cityOptions = availableFilters.cities.map((option) => ({
    value: option.value,
    label: formatOptionLabel(option),
  }));

  const contractTypeOptions = availableFilters.contractTypes.map((option) => ({
    value: option.value,
    label: formatOptionLabel(option),
  }));

  const diplomaOptions = availableFilters.diplomaTypes.map((option) => ({
    value: option.value,
    label: formatOptionLabel(option),
  }));

  const jobOfferScoringStateOptions = availableFilters.jobOfferScoringStates.map((option) => {
    const translatedLabel = option.value === 'EMPTY'
      ? 'Non score'
      : option.value === 'SCORED'
        ? 'Score'
        : option.label;

    return {
      value: option.value,
      label: `${translatedLabel} (${option.count})`,
    };
  });

  const resumeReadinessOptions = availableFilters.resumeReadinessStatuses.map((option) => {
    const translatedLabel = option.value === 'READY'
      ? 'Pret'
      : option.value === 'CV_NOT_ANALYZED'
        ? 'CV non analyse'
        : option.value === 'CV_MISSING'
          ? 'CV manquant'
          : option.label;

    return {
      value: option.value,
      label: `${translatedLabel} (${option.count})`,
    };
  });

  const languageOptions = availableFilters.languageIds;

  const languageLevelOptions = availableFilters.languageLevelIds;

  const experienceMin = availableFilters.experienceRange.min ?? 0;
  const experienceMax = availableFilters.experienceRange.max ?? 25;
  const salaryMin = availableFilters.expectedSalaryRange.min ?? 0;
  const salaryMax = availableFilters.expectedSalaryRange.max ?? 5000;
  const scoringMin = availableFilters.jobOfferScoringRange.min ?? 0;
  const scoringMax = availableFilters.jobOfferScoringRange.max ?? 100;
  const maxRating = Math.max(1, Math.ceil(availableFilters.maxRating ?? 5));

  return (
    <SearchPresetProvider currentFilters={filter} onApplyPreset={onFilterChange}>
      <FormProvider {...methods}>
        <Container>
          <FlexRow sx={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
            <SearchBarWithNumber
              onChange={onQueryChange}
              placeholder="Rechercher un talent"
              totalCount={totalDocs}
            />
            <FilterButtonStyled onClick={toggleDrawer(true)}>
              <Box sx={{ position: 'relative', height: 24 }}>
                <i className="fi-br-filter" style={{ fontSize: '20px' }} />
                {hasFilter && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: (theme) => theme.palette.error.main,
                    }}
                  />
                )}
              </Box>
            </FilterButtonStyled>
            <PresetChooseButton />
            {endAdornment}
          </FlexRow>
        </Container>
        {hasFilter && (
          <Container>
            <FlexRow>
              <PresetSaveButton />
              <FilterChips
                filter={filter}
                labels={filterLabels}
                defaultFilters={defaultSearchFilters}
                onFilterChange={onFilterChange}
                chipLabels={chipLabels}
                renderLabel={renderSearchChipLabel}
              />
            </FlexRow>
          </Container>
        )}
        <PresetMenu />
        <PresetSaveDialog />
        <FilterDrawer
          isDrawerOpen={isDrawerOpen}
          toggleDrawer={toggleDrawer}
          onApplyFilter={onApplyFilter}
          onResetFilter={onResetFilter}
        >
        {/* Job-offer specific */}
        {context === 'JOB_OFFER' && jobOfferIdExists && (
          <>
            <FormGroup>
              <FormLabel>{filterLabels.jobOfferScoringStates}</FormLabel>
              <InputAutocomplete
                id="jobOfferScoringStates"
                name="jobOfferScoringStates"
                multiple
                options={jobOfferScoringStateOptions}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>{filterLabels.resumeReadinessStatuses}</FormLabel>
              <InputAutocomplete
                id="resumeReadinessStatuses"
                name="resumeReadinessStatuses"
                multiple
                options={resumeReadinessOptions}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>{filterLabels.jobOfferScoringRange}</FormLabel>
              <InputSliderRangeStatic
                name="jobOfferScoringRange"
                min={scoringMin}
                max={scoringMax}
                defaultMin={scoringMin}
                defaultMax={scoringMax}
                units="%"
                minLabel={`${scoringMin}%`}
                maxLabel={`${scoringMax}%`}
                minDistance={0}
              />
            </FormGroup>
          </>
        )}

        {/* Professional */}
        <ProfessionFilter name="professionIds" options={availableFilters.professionIds} />
        <FormGroup>
          <FormLabel>{filterLabels.specialtyIds}</FormLabel>
          <InputAutocomplete
            id="specialtyIds"
            name="specialtyIds"
            multiple
            groupBy={(option: any) => option.groupLabel}
            options={specialtyOptions}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>{filterLabels.softSkillIds}</FormLabel>
          <InputAutocomplete
            id="softSkillIds"
            name="softSkillIds"
            multiple
            options={softSkillOptions}
          />
        </FormGroup>

        {/* Location */}
        <FormGroup>
          <FormLabel>{filterLabels.geoLocation}</FormLabel>
          <InputLocationFilter name="geoLocation" />
        </FormGroup>
        <FormGroup>
          <FormLabel>{filterLabels.cities}</FormLabel>
          <InputAutocomplete id="cities" name="cities" multiple options={cityOptions} />
        </FormGroup>
        <CountryFilter name="countries" options={availableFilters.countries} />

        {/* Experience & Salary */}
        <FormGroup>
          <FormLabel>{filterLabels.experienceRange}</FormLabel>
          <InputSliderRangeStatic
            name="experienceRange"
            min={experienceMin}
            max={experienceMax}
            defaultMin={experienceMin}
            defaultMax={experienceMax}
            units="an"
            minLabel={`${experienceMin} an`}
            maxLabel={`${experienceMax} ans`}
            minDistance={0}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>{filterLabels.expectedSalaryRange}</FormLabel>
          <InputSliderRangeStatic
            name="expectedSalaryRange"
            min={salaryMin}
            max={salaryMax}
            defaultMin={salaryMin}
            defaultMax={salaryMax}
            units="€"
            minLabel={`${salaryMin}€`}
            maxLabel={`${salaryMax}€`}
            minDistance={100}
          />
        </FormGroup>

        {/* Availability & Status */}
        <AvailabilityFilter name="availability" options={availableFilters.availability} />
        <StatusFilter name="currentStatus" options={availableFilters.currentStatus} />
        <FormGroup>
          <FormLabel>{filterLabels.contractTypes}</FormLabel>
          <InputAutocomplete
            id="contractTypes"
            name="contractTypes"
            multiple
            options={contractTypeOptions}
          />
        </FormGroup>

        {/* Languages & Education */}
        <LanguageFilter
          name="languages"
          languageOptions={languageOptions}
          levelOptions={languageLevelOptions}
        />
        <FormGroup>
          <FormLabel>{filterLabels.diplomaTypes}</FormLabel>
          <InputAutocomplete
            id="diplomaTypes"
            name="diplomaTypes"
            multiple
            options={diplomaOptions}
          />
        </FormGroup>

        {/* Favorites-only section */}
        {context === 'FAVORITES' && (
          <>
            <FormGroup>
              <FormLabel>{filterLabels.minRating}</FormLabel>
              <Box
                sx={{
                  padding: '8px 16px',
                  borderRadius: '16px',
                  background: (theme) => theme.palette.disableFormBlack.main,
                  width: 'fit-content',
                }}
              >
                <Controller
                  name="minRating"
                  control={methods.control}
                  defaultValue={0}
                  render={({ field }) => (
                    <Rating
                      {...field}
                      value={field.value ?? 0}
                      max={maxRating}
                      sx={{ color: '#ffd700', display: 'flex', alignItems: 'center', gap: '14px' }}
                      icon={<StarActiveIcon style={{ width: '24px', height: '24px' }} />}
                      emptyIcon={<StarIcon style={{ width: '24px', height: '24px' }} />}
                      onChange={(_, newValue) => field.onChange(newValue)}
                    />
                  )}
                />
              </Box>
            </FormGroup>
            <TagFilter name="tags" options={availableFilters.tags} />
            <PriorityFilter name="priority" options={availableFilters.priority} />
          </>
        )}
        </FilterDrawer>
      </FormProvider>
    </SearchPresetProvider>
  );
};

export default TalentSearchFilter;
